import { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  hideAdGroupWeightEdit,
  hidePreview,
  showPreview,
  updateAdGroupsWeight,
} from 'features/dashboard/operation/adGroupWeightEdit/adGroupWeightEditSlice'
import { Modal } from 'components/dialog/Modals'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import { integerValue, invalidNumberInput, isEmptyObject } from 'utils'
import Table from 'components/table/Table'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import useValidator from 'utils/hooks/useValidator'
import classNames from 'classnames'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import Loading from 'components/loading/Loading'
import WeightInput from 'components/form/WeightInput'
import FixedTable from 'components/table/FixedTable'

const AdGroupWeightEditModal = () => {
  const dispatch = useDispatch()
  const {
    visible,
    isSubmitting,
    previewVisible,
    isLoading,
    adGroups,
    loadingError,
    form: { pcWeight, mobileWeight, keywordExtensionWeight },
    preview: {
      pcWeight: previewPcWeight,
      mobileWeight: previewMobileWeight,
      keywordExtensionWeight: previewKeywordExtensionWeight,
    },
  } = useSelector(
    ({ dashboard }) => dashboard.operation.adGroupWeightEdit,
    shallowEqual
  )
  const [pcWeightInput, setPcWeightInput] = useState(pcWeight)
  const [mobileWeightInput, setMobileWeightInput] = useState(mobileWeight)
  const [keywordExtensionWeightInput, setKeywordExtensionWeightInput] =
    useState(keywordExtensionWeight)
  useEffect(() => {
    if (visible) {
      setPcWeightInput(pcWeight)
      setMobileWeightInput(mobileWeight)
      setKeywordExtensionWeightInput(keywordExtensionWeight)
    }
  }, [visible, pcWeight, mobileWeight, keywordExtensionWeight])
  const { errors, validate } = useValidator(
    () => ({
      pcWeight:
        (pcWeightInput === '' ||
          invalidNumberInput(pcWeightInput, 500, 10, 1)) &&
        true,
      mobileWeight:
        (mobileWeightInput === '' ||
          invalidNumberInput(mobileWeightInput, 500, 10, 1)) &&
        true,
      keywordExtensionWeight:
        (keywordExtensionWeightInput === '' ||
          invalidNumberInput(keywordExtensionWeightInput, 500, 10, 1)) &&
        true,
    }),
    [pcWeightInput, mobileWeightInput, keywordExtensionWeightInput]
  )
  const handlePreview = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        dispatch(
          showPreview({
            pcWeight: pcWeightInput,
            mobileWeight: mobileWeightInput,
            keywordExtensionWeight: keywordExtensionWeightInput,
          })
        )
      }
    } catch (ignore) {}
  }
  const handleConfirm = async ({ preventClose }) => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const { error } = await dispatch(
          updateAdGroupsWeight({
            pcWeight: pcWeightInput,
            mobileWeight: mobileWeightInput,
            keywordExtensionWeight: keywordExtensionWeightInput,
          })
        )
        if (error) {
          preventClose()
          fail(
            '광고그룹 입찰가중치 변경사항을 적용할 수 없습니다.\n다시 시도해 주세요.'
          )
        } else {
          dispatch(refreshDashboard())
          success('광고그룹 입찰가중치 변경사항이 적용되었습니다.')
        }
      }
    } catch (error) {
      preventClose()
    }
  }

  useEffect(() => {
    if (!isEmptyObject(errors)) {
      dispatch(hidePreview())
    }
  }, [dispatch, errors])
  useEffect(
    () => () => {
      dispatch(hideAdGroupWeightEdit())
    },
    [dispatch]
  )
  useEffect(() => {
    if (loadingError) {
      dispatch(hideAdGroupWeightEdit())
      failWithErrorResponse(loadingError, {
        messageMap: {
          104007: '삭제된 값이 존재합니다.\n새로고침 후 다시 시도해 주세요.',
        },
      })
    }
  }, [dispatch, loadingError])

  const preview = previewVisible && isEmptyObject(errors)
  const changed =
    pcWeightInput !== previewPcWeight ||
    mobileWeightInput !== previewMobileWeight ||
    keywordExtensionWeightInput !== previewKeywordExtensionWeight

  return (
    <Modal on={visible} className="biddingchange_layer">
      <PreviewConfirmModal
        title="광고그룹 입찰가중치 변경"
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={!changed && preview}
        confirmDisabled={changed && preview}
        handleClose={() => dispatch(hideAdGroupWeightEdit())}>
        <p className="desc_info">
          기본입찰가에 대한 가중치를 노출 디바이스와 키워드확장인 경우에 대해
          설정할 수 있습니다.
          <br />
          가중치 설정으로 입찰가 70원 이하 값 발생 시 최소 금액 70원으로
          입찰됩니다.
        </p>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <ul className="list_bidmodify">
              <li>
                <span className="tit_landing">모바일</span>
                <WeightInput
                  value={mobileWeightInput}
                  onValueChange={v => setMobileWeightInput(integerValue(v))}
                  error={errors.mobileWeight}
                  min={10}
                  max={500}
                  step={1}
                  maxLength={3}
                />
              </li>
              <li>
                <span className="tit_landing">PC</span>
                <WeightInput
                  value={pcWeightInput}
                  onValueChange={v => setPcWeightInput(integerValue(v))}
                  error={errors.pcWeight}
                  min={10}
                  max={500}
                  step={1}
                  maxLength={3}
                />
              </li>
              <li>
                <span className="tit_landing">키워드확장</span>
                <WeightInput
                  value={keywordExtensionWeightInput}
                  onValueChange={v =>
                    setKeywordExtensionWeightInput(integerValue(v))
                  }
                  error={errors.keywordExtensionWeight}
                  min={10}
                  max={500}
                  step={1}
                  maxLength={3}
                />
              </li>
            </ul>
            {!isEmptyObject(errors) && (
              <p className="txt_error">
                입찰가중치는 최소 10%부터 최대 500%내에서 설정해주세요.
              </p>
            )}
            {preview && (
              <FixedTable caption="광고그룹 입찰가중치 변경 이력 표">
                <Table.Head>
                  <Table.Column
                    title="선택 광고그룹"
                    className="inner_cell"
                    width={194}
                  />
                  <Table.Column
                    title="변경 전"
                    className="inner_cell"
                    width={171}
                  />
                  <Table.Column title="변경 후" className="inner_cell" />
                </Table.Head>
                <Table.Body>
                  {adGroups.map(
                    ({
                      id,
                      name,
                      biddingStrategy: {
                        pcWeight: pc,
                        mobileWeight: mobile,
                        keywordExtensionWeight: keywordExtension,
                      },
                    }) => (
                      <Table.Row
                        rowId={id}
                        key={id}
                        className={classNames({
                          update:
                            pc !== previewPcWeight ||
                            mobile !== previewMobileWeight ||
                            keywordExtension !== previewKeywordExtensionWeight,
                        })}>
                        <td>
                          <div className="inner_cell">
                            <span className="txt_limit2">{name}</span>
                          </div>
                        </td>
                        <td>
                          <div className="inner_cell">
                            모바일 : {mobile}%<br />
                            PC : {pc}%<br />
                            키워드확장 : {keywordExtension}%
                          </div>
                        </td>
                        <td>
                          <div className="inner_cell">
                            모바일 : {previewMobileWeight}%<br />
                            PC : {previewPcWeight}%<br />
                            키워드확장 : {previewKeywordExtensionWeight}%
                          </div>
                        </td>
                      </Table.Row>
                    )
                  )}
                </Table.Body>
              </FixedTable>
            )}
          </>
        )}
      </PreviewConfirmModal>
    </Modal>
  )
}
export default AdGroupWeightEditModal
